/**
 * 会标：用 Mapbox symbol 钉在经纬度上（随缩放不漂）。
 * HTML Marker 用 CSS translate(-50%,-100%)，父级一缩放针尖就会离地。
 * 图标锚点在水滴尖（viewBox 底边中点）。
 */

export const RALLY_PIN_SOURCE = 'zhxt-rally-pin-src';
export const RALLY_PIN_LAYER = 'zhxt-rally-pin-layer';
export const RALLY_PIN_HIT = 'zhxt-rally-pin-hit';
export const RALLY_PIN_IMAGE = 'zhxt-rally-pin';

const EMPTY = { type: 'FeatureCollection', features: [] };

/** 尖端在 (14, 36)，配合 icon-anchor: bottom */
function rallyPinSvg(fill = '#2563eb') {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
    <path d="M14 1.6C7.3 1.6 2 6.9 2 13.6c0 9.2 12 22.4 12 22.4S26 22.8 26 13.6C26 6.9 20.7 1.6 14 1.6z"
      fill="${fill}" stroke="#fff" stroke-width="1.35"/>
    <circle cx="14" cy="13.4" r="4.1" fill="#fff"/>
  </svg>`;
}

function svgImage(svg) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('会标图标加载失败'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

function emptySource() {
  return { type: 'geojson', data: EMPTY };
}

function pointData(lng, lat) {
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: [Number(lng), Number(lat)] }
    }]
  };
}

export async function ensureRallyPinLayer(map) {
  if (!map || !map.isStyleLoaded?.()) return false;
  try {
    if (!map.hasImage(RALLY_PIN_IMAGE)) {
      const img = await svgImage(rallyPinSvg());
      if (!map.hasImage(RALLY_PIN_IMAGE)) {
        map.addImage(RALLY_PIN_IMAGE, img, { pixelRatio: 1 });
      }
    }
    if (!map.getSource(RALLY_PIN_SOURCE)) {
      map.addSource(RALLY_PIN_SOURCE, emptySource());
    }
    if (!map.getLayer(RALLY_PIN_HIT)) {
      map.addLayer({
        id: RALLY_PIN_HIT,
        type: 'circle',
        source: RALLY_PIN_SOURCE,
        paint: {
          'circle-radius': 18,
          'circle-color': '#2563eb',
          'circle-opacity': 0.01
        }
      });
    }
    if (!map.getLayer(RALLY_PIN_LAYER)) {
      map.addLayer({
        id: RALLY_PIN_LAYER,
        type: 'symbol',
        source: RALLY_PIN_SOURCE,
        layout: {
          'icon-image': RALLY_PIN_IMAGE,
          'icon-anchor': 'bottom',
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-pitch-alignment': 'viewport',
          'icon-rotation-alignment': 'viewport'
        }
      });
    }
    return true;
  } catch (_e) {
    return false;
  }
}

export function setRallyPinLngLat(map, lng, lat) {
  if (!map) return;
  const src = map.getSource?.(RALLY_PIN_SOURCE);
  if (!src?.setData) return;
  const a = Number(lng);
  const b = Number(lat);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return;
  src.setData(pointData(a, b));
}

export function clearRallyPin(map) {
  const src = map?.getSource?.(RALLY_PIN_SOURCE);
  if (src?.setData) src.setData(EMPTY);
}

export function rallyPinHitAt(map, point) {
  if (!map?.queryRenderedFeatures || !point) return false;
  const layers = [RALLY_PIN_LAYER, RALLY_PIN_HIT].filter((id) => map.getLayer?.(id));
  if (!layers.length) return false;
  try {
    return (map.queryRenderedFeatures(point, { layers }) || []).length > 0;
  } catch (_e) {
    return false;
  }
}
